import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  FormHelperText
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../services/api';

interface AuditLog {
  id: number;
  user_id: number;
  action: string;
  resource_type: string;
  resource_id: number | null;
  old_values: any;
  new_values: any;
  metadata: any;
  ip_address: string;
  user_agent: string;
  created_at: string;
  user?: {
    email: string;
    first_name: string;
    last_name: string;
  };
}

interface AuditLogStats {
  overall: {
    total_logs: number;
    unique_users: number;
    unique_actions: number;
    date_range: {
      start: string;
      end: string;
    };
  };
  by_action: Array<{
    action: string;
    count: number;
  }>;
  by_resource: Array<{
    resource_type: string;
    count: number;
  }>;
  daily_activity: Array<{
    date: string;
    count: number;
  }>;
}

interface FilterOptions {
  actions: string[];
  resourceTypes: string[];
  users: Array<{
    id: number;
    email: string;
    name: string;
  }>;
}

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  
  // Filters
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    user_id: '',
    search: '',
    start_date: '',
    end_date: ''
  });

  // Debounced search value
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Fetch filter options
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters.search]);

  // Fetch logs when filters or pagination change
  useEffect(() => {
    fetchLogs();
  }, [page, rowsPerPage, filters.action, filters.resource_type, filters.user_id, debouncedSearch, filters.start_date, filters.end_date]);

  // Restore focus to search input if it was focused before update
  useEffect(() => {
    if (isSearchFocused && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
    }
  });

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/audit-logs/filters/options');
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...Object.fromEntries(
          Object.entries({
            ...filters,
            search: debouncedSearch // Use debounced search value
          }).filter(([_, value]) => value !== '')
        )
      };
      
      const response = await api.get('/audit-logs', { params });
      setLogs(response.data.logs);
      setTotalCount(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/audit-logs/stats/summary');
      setStats(response.data);
      setShowStats(true);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        ...Object.fromEntries(
          Object.entries({
            ...filters,
            search: debouncedSearch // Use debounced search value
          }).filter(([_, value]) => value !== '')
        ),
        limit: 10000
      };
      
      const response = await api.get('/audit-logs/export/csv', {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to export logs:', err);
    }
  };

  const handleFilterChange = (field: string) => (event: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setPage(0);
  };

  const getActionColor = (action: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    if (action.includes('DELETE')) return 'error';
    if (action.includes('CREATE')) return 'success';
    if (action.includes('UPDATE')) return 'primary';
    if (action.includes('LOGIN')) return 'info';
    if (action.includes('FAILURE') || action.includes('LOCKED')) return 'warning';
    return 'default';
  };

  const formatUserAgent = (userAgent: string) => {
    const match = userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)[\/\s](\d+)/);
    return match ? `${match[1]} ${match[2]}` : userAgent.substring(0, 50);
  };

  // Don't show full-page loading to prevent losing focus
  const showInitialLoading = loading && logs.length === 0 && !filters.search && !debouncedSearch;

  if (showInitialLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-testid="audit-logs-component">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="audit-logs-error">
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }} data-testid="audit-logs-filters">
        <Stack spacing={2}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={filters.action}
                onChange={handleFilterChange('action')}
                label="Action"
                data-testid="audit-logs-action-filter"
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.actions.map(action => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Resource Type</InputLabel>
              <Select
                value={filters.resource_type}
                onChange={handleFilterChange('resource_type')}
                label="Resource Type"
                data-testid="audit-logs-resource-filter"
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.resourceTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>User</InputLabel>
              <Select
                value={filters.user_id}
                onChange={handleFilterChange('user_id')}
                label="User"
                data-testid="audit-logs-user-filter"
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.users.map(user => (
                  <MenuItem key={user.id} value={user.id.toString()}>
                    {user.name} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              size="small"
              label="Search"
              value={filters.search}
              onChange={handleFilterChange('search')}
              placeholder="Search logs..."
              sx={{ minWidth: 200 }}
              inputRef={searchInputRef}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              data-testid="audit-logs-search"
              InputProps={{
                endAdornment: loading && filters.search !== '' ? (
                  <CircularProgress size={20} />
                ) : null
              }}
            />
            
            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={filters.start_date}
              onChange={handleFilterChange('start_date')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            
            <TextField
              size="small"
              type="date"
              label="End Date"
              value={filters.end_date}
              onChange={handleFilterChange('end_date')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
          </Box>
        
        <Stack direction="row" spacing={1} mt={2}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchLogs}
            variant="outlined"
            data-testid="audit-logs-refresh"
          >
            Refresh
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            variant="outlined"
            data-testid="audit-logs-export"
          >
            Export CSV
          </Button>
          <Button
            startIcon={<AssessmentIcon />}
            onClick={fetchStats}
            variant="outlined"
            data-testid="audit-logs-stats"
          >
            View Stats
          </Button>
        </Stack>
        </Stack>
      </Paper>

      {/* Logs Table */}
      <TableContainer component={Paper} data-testid="audit-logs-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Browser</TableCell>
              <TableCell align="center">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map(log => (
              <TableRow key={log.id}>
                <TableCell>
                  {format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}
                </TableCell>
                <TableCell>
                  {log.user ? (
                    <Tooltip title={log.user.email}>
                      <span>{log.user.first_name} {log.user.last_name}</span>
                    </Tooltip>
                  ) : (
                    <em>System</em>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={log.action}
                    size="small"
                    color={getActionColor(log.action)}
                  />
                </TableCell>
                <TableCell>
                  {log.resource_type}
                  {log.resource_id && ` #${log.resource_id}`}
                </TableCell>
                <TableCell>{log.ip_address}</TableCell>
                <TableCell>{formatUserAgent(log.user_agent)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => setSelectedLog(log)}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No audit logs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedLog && (
          <>
            <DialogTitle>
              Audit Log Details - {selectedLog.action}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Box display="flex" gap={3}>
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Timestamp
                    </Typography>
                    <Typography>
                      {format(new Date(selectedLog.created_at), 'PPpp')}
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      User
                    </Typography>
                    <Typography>
                      {selectedLog.user ? 
                        `${selectedLog.user.first_name} ${selectedLog.user.last_name} (${selectedLog.user.email})` : 
                        'System'
                      }
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={3}>
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Resource
                    </Typography>
                    <Typography>
                      {selectedLog.resource_type}
                      {selectedLog.resource_id && ` #${selectedLog.resource_id}`}
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      IP Address
                    </Typography>
                    <Typography>{selectedLog.ip_address}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    User Agent
                  </Typography>
                  <Typography sx={{ wordBreak: 'break-word' }}>
                    {selectedLog.user_agent}
                  </Typography>
                </Box>
                
                {selectedLog.old_values && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Old Values
                    </Typography>
                    <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(selectedLog.old_values, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                
                {selectedLog.new_values && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      New Values
                    </Typography>
                    <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(selectedLog.new_values, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                
                {selectedLog.metadata && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Metadata
                    </Typography>
                    <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(selectedLog.metadata, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedLog(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Stats Dialog */}
      <Dialog
        open={showStats}
        onClose={() => setShowStats(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Audit Log Statistics</DialogTitle>
        <DialogContent>
          {stats && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Overview
                  </Typography>
                  <Box display="flex" gap={3}>
                    <Box flex={1} textAlign="center">
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Logs
                      </Typography>
                      <Typography variant="h4">
                        {stats.overall.total_logs.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box flex={1} textAlign="center">
                      <Typography variant="subtitle2" color="text.secondary">
                        Unique Users
                      </Typography>
                      <Typography variant="h4">
                        {stats.overall.unique_users}
                      </Typography>
                    </Box>
                    <Box flex={1} textAlign="center">
                      <Typography variant="subtitle2" color="text.secondary">
                        Unique Actions
                      </Typography>
                      <Typography variant="h4">
                        {stats.overall.unique_actions}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              
              <Box display="flex" gap={2}>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Actions
                    </Typography>
                    {stats.by_action.slice(0, 5).map(item => (
                      <Box key={item.action} display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">{item.action}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {item.count}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
                
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Resources
                    </Typography>
                    {stats.by_resource.slice(0, 5).map(item => (
                      <Box key={item.resource_type} display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">{item.resource_type}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {item.count}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStats(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;